import makeTree from 'utils/makeTree';

// Implementación probada en src/api/configuracion/clavesPresupuestalesDeEgresos.js
// (makeTreeCog/normalizeCog/extractIdsCog, usada en
// src/views/Presupuestos/PresupuestosAnuales/Productos.jsx).
// Requiere el endpoint contabilidad/clasificaciones-objeto-gasto-union/, que combina del lado
// del backend el catálogo nacional (capítulo > concepto > partida genérica, `seleccionable: false`)
// con las partidas "interno" de la entidad (siempre hoja, `seleccionable: true`) en un solo listado
// plano enlazado por `clave_padre_id`.
//
// El truco: para que `makeTree` (que arbola por `clave_padre_id === id`) no confunda un `id`
// numérico del catálogo nacional con el mismo `id` numérico de una partida interna (son tablas
// distintas con sus propios autoincrementales), los nodos NO seleccionables (nacionales) se
// convierten a `id` string antes de armar el árbol; las hojas seleccionables (siempre internas)
// conservan su `id` numérico.
export const makeTreeFromClasificacionesObjetoGasto = (data) => makeTree(
  (data || [])
    .map((item) => (!item.seleccionable ? { ...item, id: `${item.id}` } : item))
    .sort((a, b) => a.id - b.id),
);

const buildDisabledTree = (data = []) => data.map((item) => {
  const clone = { ...item };
  const children = clone.children?.length && typeof clone.id === 'string'
    ? buildDisabledTree(clone.children) : [];
  if (!children.length || typeof clone.id === 'number') {
    delete clone.children;
    return ({ ...clone, disabled: !clone.seleccionable });
  }
  return ({ ...clone, children, disabled: !clone.seleccionable });
});

// El `id` crudo se repite entre nodos nacionales e internos (ver arriba), así que no sirve como
// `key`/`value` único de TreeSelect. En su lugar se usa una ruta que encierra todo el linaje
// (`root-<id>-<índice>` por nivel), garantizando unicidad sin importar de qué tabla venga el nodo.
// El `id` real se conserva aparte para poder localizar el nodo por id (columnas, edición, etc.)
// y `extractIdClasificacionObjetoGasto` recupera el id real a partir del value al guardar.
const buildPathedTree = (data, parentPath = 'root') => {
  if (!data?.length) return [];
  return data.map((item, index) => {
    const clone = { ...item };
    const currentPath = `${parentPath}-${clone.id}-${index}`;
    const children = buildPathedTree(clone.children, currentPath);
    const esHoja = !children.length;
    return {
      key: currentPath,
      value: currentPath,
      id: clone.id,
      title: `${clone.clave} - ${clone.concepto}`,
      disabled: esHoja ? clone.disabled : true,
      ...(children.length && { children }),
    };
  });
};

export const normalizeClasificacionesObjetoGasto = (tree) => buildPathedTree(
  buildDisabledTree(tree),
);

// Solo funciona sobre values generados por normalizeClasificacionesObjetoGasto
// (`root-<id>-<índice>-...-<id>-<índice>`): el id real es el penúltimo segmento.
export const extractIdClasificacionObjetoGasto = (value) => {
  const extract = (v) => {
    if (typeof v !== 'string' || !v.includes('-')) return v;
    const parts = v.split('-');
    return parts[parts.length - 2];
  };
  return Array.isArray(value) ? value.map(extract) : extract(value);
};

export default makeTreeFromClasificacionesObjetoGasto;
